// Pure helpers for the platform's location dictionary — the closed set of
// places a company picks its home city from at creation (see the `Location`
// interface in `types.ts`). The dictionary rows are seeded by a migration and
// re-ensured at boot from `SEED_LOCATIONS` here, so the seed list is the single
// source of truth for which places exist first. Zero non-stdlib imports: the
// domain layer is shared by the client and the server.

import type { LanguageCode } from './languages';
import type { Location, LocationTranslations } from './types';

/** The fields of a seed location (everything but the store-assigned id/time). */
export interface LocationSeed {
  name: string;
  translations: LocationTranslations;
  country: string;
  latitude: number;
  longitude: number;
}

/**
 * The locations provisioned first. Matched to the `location` migration's seed
 * INSERTs; the boot-time `ensureLocations` hook inserts any that a
 * non-migrated store is missing (matched by canonical `name`), never
 * overwriting an existing row — mirroring how the default company is ensured.
 *
 * `name` is the canonical (English) label; `translations` overrides it per UI
 * language (a missing language falls back to `name`). `country` is the
 * canonical (English) country name, translated for display through
 * `COUNTRY_TRANSLATIONS` (a country the map does not know degrades to its
 * English name, exactly as a city without a translation does). Coordinates are
 * WGS84.
 */
export const SEED_LOCATIONS: readonly LocationSeed[] = [
  {
    name: 'Irkutsk',
    translations: { ru: 'Иркутск', zh: '伊尔库茨克' },
    country: 'Russia',
    latitude: 52.2869,
    longitude: 104.305,
  },
  {
    name: 'Siem Reap',
    translations: { ru: 'Сиемреап', zh: '暹粒', km: 'សៀមរាប' },
    country: 'Cambodia',
    latitude: 13.3617,
    longitude: 103.8591,
  },
  // Russia — kept in the order the dictionary list supplies them (roughly by
  // size, with the Irkutsk-oblast towns grouped next to the home city). This is
  // provisioning order only: the store hands rows back sorted by canonical
  // name, and the pickers re-sort them by the localized name they display.
  {
    name: 'Moscow',
    translations: { ru: 'Москва', zh: '莫斯科' },
    country: 'Russia',
    latitude: 55.7558,
    longitude: 37.6173,
  },
  {
    name: 'Saint Petersburg',
    translations: { ru: 'Санкт-Петербург', zh: '圣彼得堡' },
    country: 'Russia',
    latitude: 59.9386,
    longitude: 30.3141,
  },
  {
    name: 'Novosibirsk',
    translations: { ru: 'Новосибирск', zh: '新西伯利亚' },
    country: 'Russia',
    latitude: 55.0084,
    longitude: 82.9357,
  },
  {
    name: 'Yekaterinburg',
    translations: { ru: 'Екатеринбург', zh: '叶卡捷琳堡' },
    country: 'Russia',
    latitude: 56.8389,
    longitude: 60.6057,
  },
  {
    name: 'Kazan',
    translations: { ru: 'Казань', zh: '喀山' },
    country: 'Russia',
    latitude: 55.7963,
    longitude: 49.1088,
  },
  {
    name: 'Krasnoyarsk',
    translations: { ru: 'Красноярск', zh: '克拉斯诺亚尔斯克' },
    country: 'Russia',
    latitude: 56.0153,
    longitude: 92.8932,
  },
  {
    name: 'Nizhny Novgorod',
    translations: { ru: 'Нижний Новгород', zh: '下诺夫哥罗德' },
    country: 'Russia',
    latitude: 56.3269,
    longitude: 44.0059,
  },
  {
    name: 'Chelyabinsk',
    translations: { ru: 'Челябинск', zh: '车里雅宾斯克' },
    country: 'Russia',
    latitude: 55.1644,
    longitude: 61.4368,
  },
  {
    name: 'Ufa',
    translations: { ru: 'Уфа', zh: '乌法' },
    country: 'Russia',
    latitude: 54.7388,
    longitude: 55.9721,
  },
  {
    name: 'Samara',
    translations: { ru: 'Самара', zh: '萨马拉' },
    country: 'Russia',
    latitude: 53.1959,
    longitude: 50.1002,
  },
  {
    name: 'Rostov-on-Don',
    translations: { ru: 'Ростов-на-Дону', zh: '顿河畔罗斯托夫' },
    country: 'Russia',
    latitude: 47.2357,
    longitude: 39.7015,
  },
  {
    name: 'Krasnodar',
    translations: { ru: 'Краснодар', zh: '克拉斯诺达尔' },
    country: 'Russia',
    latitude: 45.0355,
    longitude: 38.9753,
  },
  {
    name: 'Omsk',
    translations: { ru: 'Омск', zh: '鄂木斯克' },
    country: 'Russia',
    latitude: 54.9885,
    longitude: 73.3242,
  },
  {
    name: 'Voronezh',
    translations: { ru: 'Воронеж', zh: '沃罗涅日' },
    country: 'Russia',
    latitude: 51.672,
    longitude: 39.1843,
  },
  {
    name: 'Perm',
    translations: { ru: 'Пермь', zh: '彼尔姆' },
    country: 'Russia',
    latitude: 58.0105,
    longitude: 56.2502,
  },
  {
    name: 'Volgograd',
    translations: { ru: 'Волгоград', zh: '伏尔加格勒' },
    country: 'Russia',
    latitude: 48.708,
    longitude: 44.5133,
  },
  {
    name: 'Saratov',
    translations: { ru: 'Саратов', zh: '萨拉托夫' },
    country: 'Russia',
    latitude: 51.5336,
    longitude: 46.0343,
  },
  {
    name: 'Tyumen',
    translations: { ru: 'Тюмень', zh: '秋明' },
    country: 'Russia',
    latitude: 57.1522,
    longitude: 65.5272,
  },
  {
    name: 'Tolyatti',
    translations: { ru: 'Тольятти', zh: '陶里亚蒂' },
    country: 'Russia',
    latitude: 53.5303,
    longitude: 49.3461,
  },
  {
    name: 'Makhachkala',
    translations: { ru: 'Махачкала', zh: '马哈奇卡拉' },
    country: 'Russia',
    latitude: 42.9764,
    longitude: 47.5024,
  },
  {
    name: 'Barnaul',
    translations: { ru: 'Барнаул', zh: '巴尔瑙尔' },
    country: 'Russia',
    latitude: 53.3606,
    longitude: 83.7636,
  },
  {
    name: 'Izhevsk',
    translations: { ru: 'Ижевск', zh: '伊热夫斯克' },
    country: 'Russia',
    latitude: 56.8526,
    longitude: 53.2045,
  },
  {
    name: 'Khabarovsk',
    translations: { ru: 'Хабаровск', zh: '哈巴罗夫斯克' },
    country: 'Russia',
    latitude: 48.4827,
    longitude: 135.0838,
  },
  {
    name: 'Ulyanovsk',
    translations: { ru: 'Ульяновск', zh: '乌里扬诺夫斯克' },
    country: 'Russia',
    latitude: 54.3282,
    longitude: 48.3866,
  },
  {
    name: 'Angarsk',
    translations: { ru: 'Ангарск', zh: '安加尔斯克' },
    country: 'Russia',
    latitude: 52.5406,
    longitude: 103.8887,
  },
  {
    name: 'Shelekhov',
    translations: { ru: 'Шелехов', zh: '舍列霍夫' },
    country: 'Russia',
    latitude: 52.2119,
    longitude: 104.103,
  },
  {
    name: 'Usolye-Sibirskoye',
    translations: { ru: 'Усолье-Сибирское', zh: '西伯利亚乌索利耶' },
    country: 'Russia',
    latitude: 52.7565,
    longitude: 103.6444,
  },
  {
    name: 'Bratsk',
    translations: { ru: 'Братск', zh: '布拉茨克' },
    country: 'Russia',
    latitude: 56.1514,
    longitude: 101.634,
  },
  {
    name: 'Vladivostok',
    translations: { ru: 'Владивосток', zh: '符拉迪沃斯托克' },
    country: 'Russia',
    latitude: 43.1155,
    longitude: 131.8855,
  },
  {
    name: 'Yaroslavl',
    translations: { ru: 'Ярославль', zh: '雅罗斯拉夫尔' },
    country: 'Russia',
    latitude: 57.6261,
    longitude: 39.8845,
  },
  {
    name: 'Stavropol',
    translations: { ru: 'Ставрополь', zh: '斯塔夫罗波尔' },
    country: 'Russia',
    latitude: 45.0428,
    longitude: 41.9734,
  },
  {
    name: 'Tomsk',
    translations: { ru: 'Томск', zh: '托木斯克' },
    country: 'Russia',
    latitude: 56.4977,
    longitude: 84.9744,
  },
  {
    name: 'Kemerovo',
    translations: { ru: 'Кемерово', zh: '克麦罗沃' },
    country: 'Russia',
    latitude: 55.3547,
    longitude: 86.0873,
  },
  {
    name: 'Naberezhnye Chelny',
    translations: { ru: 'Набережные Челны', zh: '纳贝雷日涅切尔尼' },
    country: 'Russia',
    latitude: 55.7436,
    longitude: 52.3958,
  },
  {
    name: 'Orenburg',
    translations: { ru: 'Оренбург', zh: '奥伦堡' },
    country: 'Russia',
    latitude: 51.7727,
    longitude: 55.0988,
  },
  {
    name: 'Novokuznetsk',
    translations: { ru: 'Новокузнецк', zh: '新库兹涅茨克' },
    country: 'Russia',
    latitude: 53.7557,
    longitude: 87.1099,
  },
  {
    name: 'Ryazan',
    translations: { ru: 'Рязань', zh: '梁赞' },
    country: 'Russia',
    latitude: 54.6269,
    longitude: 39.6916,
  },
  {
    name: 'Cheboksary',
    translations: { ru: 'Чебоксары', zh: '切博克萨雷' },
    country: 'Russia',
    latitude: 56.1439,
    longitude: 47.2489,
  },
  {
    name: 'Kaliningrad',
    translations: { ru: 'Калининград', zh: '加里宁格勒' },
    country: 'Russia',
    latitude: 54.7104,
    longitude: 20.4522,
  },
  {
    name: 'Penza',
    translations: { ru: 'Пенза', zh: '奔萨' },
    country: 'Russia',
    latitude: 53.2007,
    longitude: 45.0046,
  },
  {
    name: 'Lipetsk',
    translations: { ru: 'Липецк', zh: '利佩茨克' },
    country: 'Russia',
    latitude: 52.6031,
    longitude: 39.5708,
  },
  {
    name: 'Kirov',
    translations: { ru: 'Киров', zh: '基洛夫' },
    country: 'Russia',
    latitude: 58.6035,
    longitude: 49.6679,
  },
  {
    name: 'Astrakhan',
    translations: { ru: 'Астрахань', zh: '阿斯特拉罕' },
    country: 'Russia',
    latitude: 46.3497,
    longitude: 48.0408,
  },
  {
    name: 'Tula',
    translations: { ru: 'Тула', zh: '图拉' },
    country: 'Russia',
    latitude: 54.1961,
    longitude: 37.6182,
  },
  {
    name: 'Sochi',
    translations: { ru: 'Сочи', zh: '索契' },
    country: 'Russia',
    latitude: 43.5855,
    longitude: 39.7231,
  },
  {
    name: 'Ulan-Ude',
    translations: { ru: 'Улан-Удэ', zh: '乌兰乌德' },
    country: 'Russia',
    latitude: 51.8335,
    longitude: 107.5841,
  },
  {
    name: 'Kursk',
    translations: { ru: 'Курск', zh: '库尔斯克' },
    country: 'Russia',
    latitude: 51.7373,
    longitude: 36.1874,
  },
  {
    name: 'Surgut',
    translations: { ru: 'Сургут', zh: '苏尔古特' },
    country: 'Russia',
    latitude: 61.254,
    longitude: 73.3962,
  },
  {
    name: 'Tver',
    translations: { ru: 'Тверь', zh: '特维尔' },
    country: 'Russia',
    latitude: 56.8587,
    longitude: 35.9176,
  },
  {
    name: 'Magnitogorsk',
    translations: { ru: 'Магнитогорск', zh: '马格尼托哥尔斯克' },
    country: 'Russia',
    latitude: 53.4186,
    longitude: 59.0472,
  },
  {
    name: 'Yakutsk',
    translations: { ru: 'Якутск', zh: '雅库茨克' },
    country: 'Russia',
    latitude: 62.0339,
    longitude: 129.7331,
  },
  {
    name: 'Bryansk',
    translations: { ru: 'Брянск', zh: '布良斯克' },
    country: 'Russia',
    latitude: 53.2521,
    longitude: 34.3717,
  },
  {
    name: 'Belgorod',
    translations: { ru: 'Белгород', zh: '别尔哥罗德' },
    country: 'Russia',
    latitude: 50.5977,
    longitude: 36.5858,
  },
];

/**
 * The location's display name in `language`: its per-language translation when
 * present (and non-blank), otherwise the canonical `name`. Never returns blank,
 * so a gap in the dictionary degrades to the English label rather than nothing.
 */
export function localizedLocationName(
  location: Location,
  language: LanguageCode,
): string {
  return location.translations[language]?.trim() || location.name;
}

/**
 * Per-UI-language names for the countries the dictionary uses, keyed by the
 * canonical (English) country name stored on the row. The country is dictionary
 * data, not UI chrome, so it is translated here rather than in the message
 * catalogues — the same rule the city names follow (see
 * `features/company-locations-photos.md`), and the only one open to the domain,
 * which may not import `src/i18n`.
 *
 * A country missing from this map — or missing the active language — falls back
 * to its canonical English name, exactly as a city without a translation does.
 */
export const COUNTRY_TRANSLATIONS: Readonly<
  Record<string, LocationTranslations>
> = {
  Russia: { ru: 'Россия', zh: '俄罗斯', km: 'រុស្ស៊ី' },
  Cambodia: { ru: 'Камбоджа', zh: '柬埔寨', km: 'កម្ពុជា' },
};

/**
 * The country's display name in `language`: its translation when one is known
 * (and non-blank), otherwise the canonical English name it was given. Returns
 * '' for a blank country, so callers can treat "no country" as falsy.
 */
export function localizedCountryName(
  country: string,
  language: LanguageCode,
): string {
  const canonical = country.trim();
  if (canonical.length === 0) return '';
  return COUNTRY_TRANSLATIONS[canonical]?.[language]?.trim() || canonical;
}

/**
 * The location's full display label in `language`: the localized city name,
 * followed by the localized country when one is known — e.g. "Иркутск,
 * Россия". Rows predating the country column (or seeds without one) degrade to
 * the bare city name rather than a dangling comma.
 */
export function localizedLocationLabel(
  location: Location,
  language: LanguageCode,
): string {
  const name = localizedLocationName(location, language);
  const country = localizedCountryName(location.country, language);
  return country.length > 0 ? `${name}, ${country}` : name;
}

/**
 * Order two locations by the name they are *displayed* under in `language`,
 * using that language's collation (so Cyrillic, Han and Latin labels each sort
 * the way a reader of that language expects). Ties break on the localized
 * country and then on `id`, so the order is total and stable whatever the
 * store hands back.
 *
 * The store already sorts by canonical (English) name — this is the display
 * counterpart, because "Yekaterinburg" and "Екатеринбург" do not land in the
 * same place in their respective alphabets.
 */
export function compareLocationsByName(
  a: Location,
  b: Location,
  language: LanguageCode,
): number {
  const collator = new Intl.Collator(language);
  const byName = collator.compare(
    localizedLocationName(a, language),
    localizedLocationName(b, language),
  );
  if (byName !== 0) return byName;
  const byCountry = collator.compare(
    localizedCountryName(a.country, language),
    localizedCountryName(b.country, language),
  );
  return byCountry !== 0 ? byCountry : a.id - b.id;
}

/**
 * Whether `location` matches a picker's search `query` in `language`. Matched
 * against the whole `"City, Country"` label a reader sees, and against the
 * canonical English pair — so someone on a Russian or Chinese UI can still
 * find a city by typing the English name they know it by. A blank query
 * matches everything.
 *
 * Matching the full label, not its halves, matters: it is the string the picker
 * renders *and* the one it writes back into the input on selection, so editing
 * a chosen city ("Иркутск, Россия" → backspace) must keep matching rather than
 * fall off the end of a half.
 */
export function locationMatchesQuery(
  location: Location,
  query: string,
  language: LanguageCode,
): boolean {
  const needle = query.trim().toLowerCase();
  if (needle.length === 0) return true;
  const canonical = location.country.trim();
  const candidates = [
    localizedLocationLabel(location, language),
    canonical.length > 0 ? `${location.name}, ${canonical}` : location.name,
  ];
  return candidates.some((text) => text.toLowerCase().includes(needle));
}

/**
 * A copy of `locations` ordered by their display name in `language` (see
 * `compareLocationsByName`). Never mutates the input, so a React list held in
 * state stays intact.
 */
export function sortLocationsByName(
  locations: readonly Location[],
  language: LanguageCode,
): Location[] {
  return [...locations].sort((a, b) => compareLocationsByName(a, b, language));
}
